import { useEffect } from 'react';
import {
    Theme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { FormProvider, useForm } from 'react-hook-form';
import FormTextField from '../components/formFields/FormTextField';

const useStyles = makeStyles()((theme: Theme) => ({
    dialogContainer: {
        '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '802px',
            boxSizing: 'border-box',
            padding: '25px',
            gap: '10px',
            border: `1px solid ${theme.palette.secondary.dark}`,
            borderRadius: '16px',
            backgroundColor: theme.palette.primary.light,
        },

        '& .MuiDialogContentText-root': {
            color: theme.palette.secondary.main,
        },
    },

    dialogTitle: {
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '34px',
        lineHeight: '40.8px',
        padding: '0px',
        color: theme.palette.common.white,
    },

    dialogContentText: {
        fontFamily: 'Poppins',
        fontWeight: '600',
        fontSize: '20px',
        lineHeight: '24px',
    },

    dialogContentValue: {
        fontFamily: 'Poppins',
        fontWeight: '600',
        fontSize: '20px',
        lineHeight: '24px',
        color: theme.palette.common.white,
    },

    dialogActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',

        '@media (max-width: 550px)': {
            flexDirection: 'column',
            gap: '16px',

            '@media (max-width: 550px)': {
                paddingRight: '16px',
            },
        },
    },

    defaultButtonStyle: {
        height: '48px',
        maxWidth: '416px',
        width: '100%',
        borderRadius: '12px',
        fontFamily: 'Poppins',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '19.2px',
        textTransform: 'none',
        color: theme.palette.common.white,

        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.08)',
            background: 'rgba(255, 255, 255, 0.08)',
        },

        '@media (max-width: 550px)': {
            maxWidth: '100%',
        },
    },

    submitButton: {
        background: theme.palette.info.main,

        '&:hover': {
            background: theme.palette.info.main,
        },
    },

    cancelButton: {
        background: theme.palette.info.dark,

        '&:hover': {
            background: theme.palette.info.dark,
        },
    },
}));

interface ApprovalPopupProps {
    isOpen: boolean;
    onClose: () => void;
    isApproval?: boolean;
    onSubmit?: (comment: string, status: string) => void;
}

const ApprovalPopup = ({
    isOpen,
    onClose,
    isApproval,
    onSubmit,
}: ApprovalPopupProps) => {
    const { classes } = useStyles();


    const formMethods = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            comment: '',
        },
    });

    const {
        watch,
        getValues,
        reset,
    } = formMethods;

    const commentValue = watch('comment');

    useEffect(() => {
        if (isOpen) {
            reset({ comment: '' });
        }
    }, [isOpen, reset]);

    const handleSubmit = () => {
        if (onSubmit) {
            const status = isApproval ? 'APPROVE' : 'REQUEST_CHANGES';

            onSubmit(
                getValues('comment'),
                status
            );
        }

        reset({ comment: '' });
        onClose();
    };

    const handleClose = () => {
        reset({ comment: '' });
        onClose();
    };

    return (
        <Dialog
            className={classes.dialogContainer}
            open={isOpen}
            onClose={handleClose}
        >
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    top: 32,
                    right: 32,
                    color: theme.palette.common.white,
                })}
            >
                <CloseIcon />
            </IconButton>

            <DialogTitle className={classes.dialogTitle}>
                {isApproval ? 'Approve Settlement' : 'Request Changes'}
            </DialogTitle>

            <FormProvider {...formMethods}>
                <DialogContent>
                    <DialogContentText
                        className={classes.dialogContentText}
                        mt={3}
                    >
                        {isApproval
                            ? 'You are confirming this settlement is correct and ready for payment.'
                            : 'Describe what needs to be corrected before this settlement can be approved.'}
                    </DialogContentText>

                    <DialogContentText
                        className={classes.dialogContentValue}
                        mt={3}
                    >
                        <FormTextField
                            name="comment"
                            label={isApproval ? 'Note (Optional)' : 'Note *'}
                            isMultiline
                            isDescription
                        />
                    </DialogContentText>
                </DialogContent>
            </FormProvider>

            <DialogActions
                className={classes.dialogActions}
                disableSpacing
            >
                <Button
                    className={clsx(
                        classes.defaultButtonStyle,
                        classes.submitButton
                    )}
                    onClick={handleSubmit}
                    disabled={!isApproval && !commentValue?.trim()}
                >
                    {isApproval
                        ? 'Approve Settlement'
                        : 'Request Changes'}
                </Button>

                <Button
                    className={clsx(
                        classes.defaultButtonStyle,
                        classes.cancelButton
                    )}
                    onClick={handleClose}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApprovalPopup;
